import { Link, useParams } from 'react-router-dom';
import PageShell from '@/components/feature/PageShell';
import FinalCta from '@/components/feature/FinalCta';
import Container from '@/components/base/Container';
import Eyebrow from '@/components/base/Eyebrow';
import ImageReveal from '@/components/base/ImageReveal';
import TitleReveal from '@/components/base/TitleReveal';
import FaqList from '@/components/feature/FaqList';
import NotFound from '@/pages/NotFound';
import { services, steps, serviceCutouts } from '@/mocks/services';
import PriceNote from '@/components/feature/PriceNote';
import { usePublicPricing } from '@/lib/pricing';

export default function ServiceDetailPage() {
  const { slug } = useParams();
  const pricing = usePublicPricing();
  const service = services.find((s) => s.slug === slug);
  if (!service) return <NotFound />;
  const others = services.filter((s) => s.slug !== service.slug);

  return (
    <PageShell>
      <section className="bg-background-50 pt-10 md:pt-16 pb-16 md:pb-24">
        <Container>
          <nav className="flex flex-wrap items-center gap-2 text-xs text-foreground-600">
            <Link to="/" className="hover:text-foreground-950">Home</Link>
            <span>/</span>
            <Link to="/services" className="hover:text-foreground-950">Services</Link>
            <span>/</span>
            <span className="text-foreground-950">{service.name}</span>
          </nav>
          <div className="mt-10 md:mt-14 grid grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-16">
            <ImageReveal key={service.slug} className="h-[380px] sm:h-[480px] lg:h-[600px] w-full overflow-hidden">
              <img src={serviceCutouts[service.slug] || service.image} alt={service.name} title={`${service.name} - in-home repair`} className="h-full w-full object-contain object-center p-8 md:p-14" />
            </ImageReveal>
            <div className="flex flex-col">
              <Eyebrow label="Service" />
              <TitleReveal key={service.slug} as="h1" className="mt-6 text-[38px] md:text-[56px] font-medium leading-[1.02] tracking-[-0.03em] text-foreground-950">{service.name}</TitleReveal>
              <p className="mt-6 max-w-md text-[15px] md:text-base leading-relaxed text-foreground-600">{service.description}</p>
              <PriceNote pricing={pricing} slug={service.slug} showFee={false} showLink className="mt-6 max-w-md" />
              <Link to={`/book?appliance=${service.slug}`} className="mt-8 inline-flex h-12 w-fit items-center whitespace-nowrap rounded-full bg-primary-500 px-7 text-sm text-white hover:bg-primary-600 transition-colors">
                Book a Repair
              </Link>
              <div className="mt-12 lg:mt-auto lg:pt-12">
                <h2 className="text-xs uppercase tracking-[0.14em] text-foreground-600">Common issues we look at</h2>
                <ul className="mt-4">
                  {service.issues.map((issue) => (
                    <li key={issue} className="flex items-center gap-4 border-t border-background-200 py-4 text-[15px] text-foreground-950 last:border-b">
                      <span className="h-1.5 w-1.5 rounded-full bg-primary-500" />
                      {issue}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </Container>
      </section>

      <section className="bg-background-100 py-16 md:py-24">
        <Container>
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 lg:gap-16">
            <Eyebrow label="What to Expect" />
            <h2 className="text-[30px] md:text-[44px] font-medium leading-[1.05] tracking-[-0.025em] text-foreground-950">From request to repair.</h2>
          </div>
          <div className="mt-12 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
            {steps.map((s, i) => (
              <div key={s.title} className={`rounded-lg p-6 ${i === 4 ? 'bg-primary-500 text-white' : 'bg-background-50'}`}>
                <span className={`text-xs ${i === 4 ? 'text-white/80' : 'text-foreground-600'}`}>{`0${i + 1}.`}</span>
                <h3 className="mt-6 text-base font-medium">{s.title}</h3>
                <p className={`mt-2 text-sm leading-relaxed ${i === 4 ? 'text-white/85' : 'text-foreground-600'}`}>{s.text}</p>
              </div>
            ))}
          </div>
        </Container>
      </section>

      <section className="bg-background-50 py-16 md:py-24">
        <Container>
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-16">
            <div>
              <Eyebrow label="FAQ" />
              <h2 className="mt-6 text-[30px] md:text-[44px] font-medium leading-[1.05] tracking-[-0.025em] text-foreground-950">{`${service.short} questions.`}</h2>
              <Link to={`/book?appliance=${service.slug}`} className="mw-btn mt-8 inline-flex h-11 items-center whitespace-nowrap rounded-full border border-background-200 px-6 text-sm text-foreground-950 hover:border-primary-500 hover:text-primary-600">
                Book a {service.short} Repair <i className="ri-arrow-right-line mw-arrow ml-2" />
              </Link>
            </div>
            <FaqList key={service.slug} items={service.faqs} />
          </div>
        </Container>
      </section>

      <section className="bg-background-100 py-16 md:py-24">
        <Container>
          <Eyebrow label="Other Services" />
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 border-t border-background-200">
            {others.map((s) => (
              <Link key={s.slug} to={`/services/${s.slug}`} className="group flex items-center justify-between gap-4 border-b border-background-200 py-5 lg:pr-6">
                <span className="text-[15px] text-foreground-950 group-hover:text-primary-600 transition-colors">{s.name}</span>
                <i className="ri-arrow-right-line text-primary-600" />
              </Link>
            ))}
          </div>
        </Container>
      </section>
      <FinalCta />
    </PageShell>
  );
}